import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Snackbar
} from '@mui/material';
import {
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  AccountBalance as BankIcon,
  LocalHospital as HealthIcon,
  Home as HomeIcon,
  Receipt as TaxIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { governmentDeductionsService, GovernmentDeduction, GovernmentDeductionSummary } from '../services/governmentDeductionsService';
import { governmentDeductionsExportUtils } from '../utils/governmentDeductionsExportUtils';
import { formatCurrency } from '../utils/currencyutils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`deductions-tabpanel-${index}`}
      aria-labelledby={`deductions-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `deductions-tab-${index}`,
    'aria-controls': `deductions-tabpanel-${index}`,
  };
}

const GovernmentDeductions: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [summary, setSummary] = useState<GovernmentDeductionSummary | null>(null);
  const [sssDeductions, setSssDeductions] = useState<GovernmentDeduction[]>([]);
  const [philhealthDeductions, setPhilhealthDeductions] = useState<GovernmentDeduction[]>([]);
  const [pagibigDeductions, setPagibigDeductions] = useState<GovernmentDeduction[]>([]);
  const [taxDeductions, setTaxDeductions] = useState<GovernmentDeduction[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    loadDeductionsData();
  }, []);

  const loadDeductionsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load summary and all deduction types
      const [summaryData, sssData, philhealthData, pagibigData, taxData] = await Promise.all([
        governmentDeductionsService.getDeductionsSummary(),
        governmentDeductionsService.getSSSDeductions(),
        governmentDeductionsService.getPhilHealthDeductions(),
        governmentDeductionsService.getPagIBIGDeductions(),
        governmentDeductionsService.getTaxDeductions()
      ]);

      setSummary(summaryData);
      setSssDeductions(sssData);
      setPhilhealthDeductions(philhealthData);
      setPagibigDeductions(pagibigData);
      setTaxDeductions(taxData);
    } catch (err) {
      console.error('Error loading deductions data:', err);
      setError('Failed to load government deductions data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleExport = async (type: 'sss' | 'philhealth' | 'pagibig' | 'tax' | 'all' | 'summary') => {
    try {
      setExportLoading(true);
      
      switch (type) {
        case 'sss':
          governmentDeductionsExportUtils.exportSSS(sssDeductions);
          setSnackbar({ open: true, message: 'SSS deductions exported successfully!', severity: 'success' });
          break;
        case 'philhealth':
          governmentDeductionsExportUtils.exportPhilHealth(philhealthDeductions);
          setSnackbar({ open: true, message: 'PhilHealth deductions exported successfully!', severity: 'success' });
          break;
        case 'pagibig':
          governmentDeductionsExportUtils.exportPagIBIG(pagibigDeductions);
          setSnackbar({ open: true, message: 'Pag-IBIG deductions exported successfully!', severity: 'success' });
          break;
        case 'tax':
          governmentDeductionsExportUtils.exportTax(taxDeductions);
          setSnackbar({ open: true, message: 'Tax deductions exported successfully!', severity: 'success' });
          break;
        case 'all':
          if (summary) {
            governmentDeductionsExportUtils.exportAllDeductions(summary.deductions);
            setSnackbar({ open: true, message: 'All government deductions exported successfully!', severity: 'success' });
          }
          break;
        case 'summary':
          if (summary) {
            governmentDeductionsExportUtils.exportSummary(summary);
            setSnackbar({ open: true, message: 'Summary report exported successfully!', severity: 'success' });
          }
          break;
      }
    } catch (err) {
      console.error('Export error:', err);
      setSnackbar({ open: true, message: 'Export failed. Please try again.', severity: 'error' });
    } finally {
      setExportLoading(false);
    }
  };

  const renderDeductionsTable = (deductions: GovernmentDeduction[], type: 'sss' | 'philhealth' | 'pagibig' | 'tax') => {
    const getAmount = (deduction: GovernmentDeduction) => {
      switch (type) {
        case 'sss': return deduction.sss_contribution;
        case 'philhealth': return deduction.philhealth_contribution;
        case 'pagibig': return deduction.pagibig_contribution;
        case 'tax': return deduction.withholding_tax;
        default: return 0;
      }
    };

    const getIdNumber = (deduction: GovernmentDeduction) => {
      switch (type) {
        case 'sss': return deduction.sss_number;
        case 'philhealth': return deduction.philhealth_number;
        case 'pagibig': return deduction.pagibig_number;
        case 'tax': return deduction.tin_number;
        default: return '';
      }
    };

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Employee #</TableCell>
              <TableCell>Employee Name</TableCell>
              <TableCell>{type.toUpperCase()} Number</TableCell>
              <TableCell align="right">Monthly Salary</TableCell>
              <TableCell align="right">Contribution/Tax</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deductions.map((deduction) => (
              <TableRow key={deduction.employee_id}>
                <TableCell>{deduction.employee_number}</TableCell>
                <TableCell>{deduction.employee_name}</TableCell>
                <TableCell>{getIdNumber(deduction) || 'Not provided'}</TableCell>
                <TableCell align="right">{formatCurrency(deduction.salary)}</TableCell>
                <TableCell align="right">{formatCurrency(getAmount(deduction))}</TableCell>
                <TableCell align="center">
                  <Chip 
                    label="Active" 
                    color="success" 
                    size="small" 
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadDeductionsData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Government Deductions
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Manage and export government deductions for SSS, PhilHealth, Pag-IBIG, and Tax
      </Typography>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Employees</Typography>
                </Box>
                <Typography variant="h4">{summary.total_employees}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <MoneyIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">SSS</Typography>
                </Box>
                <Typography variant="h4">{formatCurrency(summary.total_sss)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <HealthIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">PhilHealth</Typography>
                </Box>
                <Typography variant="h4">{formatCurrency(summary.total_philhealth)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <HomeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Pag-IBIG</Typography>
                </Box>
                <Typography variant="h4">{formatCurrency(summary.total_pagibig)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <TaxIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Tax</Typography>
                </Box>
                <Typography variant="h4">{formatCurrency(summary.total_tax)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Export Buttons */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Export Options
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('sss')}
              disabled={exportLoading || sssDeductions.length === 0}
            >
              Export SSS
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('philhealth')}
              disabled={exportLoading || philhealthDeductions.length === 0}
            >
              Export PhilHealth
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('pagibig')}
              disabled={exportLoading || pagibigDeductions.length === 0}
            >
              Export Pag-IBIG
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('tax')}
              disabled={exportLoading || taxDeductions.length === 0}
            >
              Export Tax
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('all')}
              disabled={exportLoading || !summary || summary.deductions.length === 0}
            >
              Export All
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('summary')}
              disabled={exportLoading || !summary}
            >
              Export Summary
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Tabs for different deduction types */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="deductions tabs">
            <Tab label={`SSS (${sssDeductions.length})`} {...a11yProps(0)} />
            <Tab label={`PhilHealth (${philhealthDeductions.length})`} {...a11yProps(1)} />
            <Tab label={`Pag-IBIG (${pagibigDeductions.length})`} {...a11yProps(2)} />
            <Tab label={`Tax (${taxDeductions.length})`} {...a11yProps(3)} />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            SSS Contributions
          </Typography>
          {renderDeductionsTable(sssDeductions, 'sss')}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            PhilHealth Contributions
          </Typography>
          {renderDeductionsTable(philhealthDeductions, 'philhealth')}
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Pag-IBIG Contributions
          </Typography>
          {renderDeductionsTable(pagibigDeductions, 'pagibig')}
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Withholding Tax
          </Typography>
          {renderDeductionsTable(taxDeductions, 'tax')}
        </TabPanel>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GovernmentDeductions;